/*
 * Pure record-value validation core, shared by the control-plane channel
 * routes AND the electric-runtime agent workspace tools — previously the
 * tools bypassed validation entirely, so agent writes could store typed
 * garbage the routes would have rejected (review finding 5, 2026-06-11).
 * Reference-existence checks need a DB and stay caller-side; everything
 * else is a pure function of the field definitions and the values.
 */
import { channelFieldLookup } from "./channel-field-lookup.js";
const URL_PATTERN = /^https?:\/\/\S+$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
function fieldOptions(config) {
    const options = config?.options;
    if (!Array.isArray(options))
        return [];
    // Select options serialize as plain strings OR {label, value} objects
    // depending on which surface created the field; match on the value either
    // way (naive String() turned objects into "[object Object]" and rejected
    // every legitimate select write).
    return options
        .map((option) => option !== null && typeof option === "object"
        ? String(option.value ?? option.label ?? "")
        : String(option))
        .filter(Boolean);
}
export function referenceObjectTypeIdFromConfig(config) {
    const target = config?.objectTypeId ?? config?.referenceObjectTypeId ?? config?.targetObjectTypeId;
    return typeof target === "string" && target.trim() ? target : undefined;
}
function isParseableDate(value) {
    if (typeof value === "number")
        return Number.isFinite(value);
    if (typeof value !== "string" || !value.trim())
        return false;
    return !Number.isNaN(Date.parse(value));
}
export function validateValuesAgainstFields(fields, values, requireRequired) {
    const byKey = new Map(fields.map((field) => [field.fieldKey, field]));
    const problems = [];
    const referenceChecks = [];
    for (const key of Object.keys(values)) {
        if (!byKey.has(key))
            problems.push(`unknown field "${key}"`);
    }
    if (requireRequired) {
        for (const field of fields) {
            if (!field.required || channelFieldLookup(field.config))
                continue;
            const value = values[field.fieldKey];
            if (value === undefined || value === null || (typeof value === "string" && !value.trim())
                || (Array.isArray(value) && value.length === 0)) {
                problems.push(`required field "${field.fieldKey}" is missing`);
            }
        }
    }
    for (const [key, value] of Object.entries(values)) {
        const field = byKey.get(key);
        if (!field)
            continue;
        if (value === undefined)
            continue;
        if (channelFieldLookup(field.config)) {
            problems.push(`field "${key}" is filled from another data set; change it there`);
            continue;
        }
        if (value === null || (typeof value === "string" && !value.trim() && field.fieldType !== "text")) {
            if (field.required)
                problems.push(`required field "${key}" cannot be cleared`);
            continue;
        }
        switch (field.fieldType) {
            case "text":
            case "user":
            case "file":
            case "artifact":
            case "session":
                if (typeof value !== "string")
                    problems.push(`field "${key}" must be a string`);
                break;
            case "url":
                if (typeof value !== "string" || !URL_PATTERN.test(value)) {
                    problems.push(`field "${key}" must be an http(s) URL`);
                }
                break;
            case "email":
                if (typeof value !== "string" || !EMAIL_PATTERN.test(value)) {
                    problems.push(`field "${key}" must be an email address`);
                }
                break;
            case "number":
            case "currency":
            case "percent":
                if (typeof value !== "number" || !Number.isFinite(value)) {
                    problems.push(`field "${key}" must be a finite number`);
                }
                break;
            case "boolean":
                if (typeof value !== "boolean")
                    problems.push(`field "${key}" must be a boolean`);
                break;
            case "date":
            case "datetime":
                if (!isParseableDate(value)) {
                    problems.push(`field "${key}" must be an ISO date string or epoch milliseconds`);
                }
                break;
            case "select": {
                const options = fieldOptions(field.config);
                if (typeof value !== "string" || !options.includes(value)) {
                    problems.push(`field "${key}" must be one of: ${options.join(", ") || "(no options configured)"}`);
                }
                break;
            }
            case "multiselect": {
                const options = fieldOptions(field.config);
                const list = Array.isArray(value) ? value : null;
                if (!list || list.some((entry) => typeof entry !== "string" || !options.includes(entry))) {
                    problems.push(`field "${key}" must be an array of: ${options.join(", ") || "(no options configured)"}`);
                }
                break;
            }
            case "multiuser": {
                const list = Array.isArray(value) ? value : null;
                if (!list || list.some((entry) => typeof entry !== "string" || !entry.trim())) {
                    problems.push(`field "${key}" must be an array of organization user ids`);
                }
                break;
            }
            case "reference":
                if (typeof value !== "string" || !value.trim()) {
                    problems.push(`field "${key}" must be a record id`);
                }
                else {
                    referenceChecks.push({ key, id: value, target: referenceObjectTypeIdFromConfig(field.config) });
                }
                break;
            case "json":
                break;
            default:
                problems.push(`field "${key}" has unsupported type "${field.fieldType}"`);
        }
    }
    return { problems, referenceChecks };
}
